// frontend/src/contexts/SidebarPrefsContext.js
//
// Per-account sidebar preferences, stored in UserProfile.ui_preferences via
// /api/auth/preferences/ so they follow the user between terminals.
//
//   sidebarPinned  - keep the sidebar permanently open with the page beside it
//                    (default off; only takes effect on desktop widths)
//   sidebarColors  - colour-code sections in the sidebar (dashboard is always coloured)
//                    (default ON so users discover the toggle)

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useAuth } from './AuthContext';

const SidebarPrefsContext = createContext(null);

const DEFAULTS = { sidebarPinned: false, sidebarColors: true };

const readPrefs = (user) => {
  const stored = user?.ui_preferences || {};
  return {
    sidebarPinned: stored.sidebarPinned ?? DEFAULTS.sidebarPinned,
    sidebarColors: stored.sidebarColors ?? DEFAULTS.sidebarColors
  };
};

export const SidebarPrefsProvider = ({ children }) => {
  const { user, isAuthenticated, api } = useAuth();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [prefs, setPrefs] = useState(() => readPrefs(user));

  // Re-sync whenever the logged-in user (and their server-side prefs) changes
  useEffect(() => {
    setPrefs(readPrefs(user));
  }, [user]);

  const savePref = useCallback(
    async (key, value) => {
      setPrefs(prev => ({ ...prev, [key]: value }));
      if (!isAuthenticated) return;
      try {
        await api.patch('/api/auth/preferences/', { [key]: value });
        // Keep the user object's copy current so a re-sync doesn't revert it
        if (user?.ui_preferences) user.ui_preferences[key] = value;
      } catch (err) {
        console.error(`Failed to save preference ${key}:`, err);
      }
    },
    [api, isAuthenticated, user]
  );

  const value = {
    pinned: prefs.sidebarPinned,
    colors: prefs.sidebarColors,
    // Pinned layout only on desktop; narrow screens fall back to the overlay
    pinnedActive: isAuthenticated && prefs.sidebarPinned && isDesktop,
    isDesktop,
    setPinned: (v) => savePref('sidebarPinned', v),
    setColors: (v) => savePref('sidebarColors', v)
  };

  return (
    <SidebarPrefsContext.Provider value={value}>
      {children}
    </SidebarPrefsContext.Provider>
  );
};

export const useSidebarPrefs = () => {
  const ctx = useContext(SidebarPrefsContext);
  if (!ctx) {
    throw new Error('useSidebarPrefs must be used within a SidebarPrefsProvider');
  }
  return ctx;
};

export const SIDEBAR_WIDTH = 280;
